/**
 * Contacts 联系人列表页：搜索 / 收藏·需联系·归档筛选 / 排序 / 分页 + 新建（ContactForm）。
 * Contact：intimacyLevel 1-5、isFavorite、lastContactAt/nextContactAt、groups。
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useContacts } from '../hooks/useContacts';
import { ContactForm } from '../components/ContactForm';
import {
  Card, Button, Input, Select, Badge, Spinner, Pagination, EmptyState, errMsg,
} from '../components/ui';
import { Icon } from '../components/icons';
import type { Contact, ContactSort } from '../lib/types';

const SORT_OPTIONS: { value: ContactSort; label: string }[] = [
  { value: 'recent', label: '最近更新' },
  { value: 'name', label: '按姓名' },
  { value: 'intimacy', label: '按亲密度' },
  { value: 'nextContact', label: '按下次联系' },
  { value: 'created', label: '按创建时间' },
];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function ContactsPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<ContactSort>('recent');
  const [favOnly, setFavOnly] = useState(false);
  const [needsContact, setNeedsContact] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);

  const { data, isLoading, error } = useContacts({
    q: q.trim() || undefined,
    sort,
    favorite: favOnly ? 'true' : undefined,
    needsContact: needsContact ? 'true' : undefined,
    archived: showArchived ? 'true' : 'false',
    page,
    pageSize: 20,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">人脉</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            管理联系人、互动记录与重要日期
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)}>
          <Icon name="plus" className="mr-1 h-4 w-4" />新建联系人
        </Button>
      </div>

      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Icon name="search" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="搜索姓名、公司、备注…"
              className="pl-9"
            />
          </div>
          <Select value={sort} onChange={(e) => setSort(e.target.value as ContactSort)} className="w-36">
            {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <button
            type="button"
            onClick={() => { setFavOnly((v) => !v); setPage(1); }}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              favOnly
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
            }`}
          >
            <Icon name="star" className="mr-1 inline h-3.5 w-3.5" />收藏
          </button>
          <button
            type="button"
            onClick={() => { setNeedsContact((v) => !v); setPage(1); }}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              needsContact
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
            }`}
          >
            需联系
          </button>
          <button
            type="button"
            onClick={() => { setShowArchived((v) => !v); setPage(1); }}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              showArchived
                ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
            }`}
          >
            已归档
          </button>
        </div>
      </Card>

      {isLoading && <div className="flex justify-center py-12"><Spinner className="h-8 w-8" /></div>}
      {error && <Card><p className="text-sm text-red-600 dark:text-red-400">{errMsg(error)}</p></Card>}

      {!isLoading && !error && (data?.items?.length ?? 0) === 0 && (
        <EmptyState
          icon={<Icon name="contacts" className="mx-auto h-10 w-10" />}
          title="暂无联系人"
          description="添加你的第一位联系人"
          action={<Button size="sm" onClick={() => setFormOpen(true)}>新建联系人</Button>}
        />
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(data?.items ?? []).map((c: Contact) => (
          <Card key={c.id} className="p-4">
            <Link to={`/contacts/${c.id}`} className="block">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-base font-semibold text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                  {c.name.slice(0, 1)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate font-medium text-slate-900 dark:text-white">{c.name}</p>
                    {c.isFavorite && <Icon name="star" className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
                    {c.archivedAt && <Badge className="bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">已归档</Badge>}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                    {[c.company, c.position].filter(Boolean).join(' · ') || '未填写单位'}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <Badge className="bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300">
                      亲密度 {'★'.repeat(Math.min(5, Math.max(1, c.intimacyLevel)))}
                    </Badge>
                    {(c.groups ?? []).slice(0, 2).map((g) => (
                      <Badge key={g.id} className="bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        {g.name}
                      </Badge>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                    上次联系 {fmtDate(c.lastContactAt)} · 下次 {fmtDate(c.nextContactAt)}
                  </p>
                </div>
              </div>
            </Link>
            <div className="mt-3 flex gap-2">
              {c.phone && (
                <Button variant="secondary" size="sm" onClick={() => navigate(`/contacts/${c.id}`)}>
                  <Icon name="user" className="mr-1 h-3.5 w-3.5" />详情
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {data && data.totalPages > 1 && (
        <Pagination page={page} totalPages={data.totalPages} onPage={setPage} />
      )}

      <ContactForm open={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}
